import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Battery, 
  Car, 
  Thermometer, 
  WashingMachine, 
  Snowflake, 
  Waves,
  CheckCircle,
  ArrowRight,
  Zap,
  TrendingUp,
  Home,
  Sun
} from "lucide-react";
import { track } from "@/lib/analytics";
import energyAppImage from "/lovable-uploads/0b6b71bd-d3b0-4f8d-8396-0d2dd4f86180.png";

const energyOptimizations = [
  {
    icon: Car,
    title: "Elektrische auto opladen",
    description: "Op het juiste moment laden met zonne-energie"
  },
  {
    icon: Thermometer,
    title: "Warmtepomp boiler",
    description: "Gebruiken als thermische energieopslag"
  },
  {
    icon: WashingMachine,
    title: "Wasmachine aanzetten",
    description: "Automatisch starten bij zonne-overschot"
  },
  {
    icon: Snowflake,
    title: "Preventief koelen",
    description: "Woning voorkoelen op warme dagen"
  },
  {
    icon: Waves,
    title: "Zwembadverwarming",
    description: "Activeren bij voldoende zonne-energie"
  }
];

const energyStats = [
  {
    value: "75%",
    label: "Hogere zelfconsumptie",
    icon: TrendingUp
  },
  {
    value: "45%", 
    label: "Energiekostenbesparing",
    icon: Zap
  },
  {
    value: "24/7",
    label: "Automatische optimalisatie",
    icon: Home
  }
];

export const EnergyManagementSection = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 relative">
        <div className="max-w-7xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 backdrop-blur-sm text-primary px-6 py-3 rounded-full text-sm font-medium mb-6 border border-primary/20">
              <Sun className="h-5 w-5" />
              Smart Energie Management
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6 bg-gradient-to-r from-primary via-primary-glow to-secondary bg-clip-text text-transparent">
              Maak je zonnepanelen slim<br />met Loxone
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Verhoog je zelfconsumptie en verlaag je energiekosten drastisch met intelligent energiebeheer
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            
            {/* Left: Energy App Screenshot */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl blur-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-700"></div>
              
              {/* Direct Image Display */}
              <div className="relative transform group-hover:scale-105 transition-transform duration-500">
                <img 
                  src={energyAppImage}
                  alt="Loxone Energy Flow Monitor - Real-time energiebeheer dashboard"
                  className="w-full h-auto rounded-3xl shadow-2xl"
                />
                
                {/* Floating Stats */}
                <div className="absolute -right-6 top-1/4 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-primary/10 group-hover:translate-x-2 transition-transform duration-500">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1">2.4 kW</div>
                    <div className="text-xs text-muted-foreground">Solar Production</div>
                  </div>
                </div>
                
                <div className="absolute -left-6 bottom-1/3 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-secondary/10 group-hover:-translate-x-2 transition-transform duration-500">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-secondary mb-1">85%</div>
                    <div className="text-xs text-muted-foreground">Self-Consumption</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-8">
              
              {/* Problem Statement */}
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-semibold text-foreground">
                  Het probleem met traditionele zonnepanelen
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Helaas is het onmogelijk om alle zelfgeproduceerde stroom van je zonnepanelen te verbruiken. 
                  Een deel gaat onvermijdelijk naar het stroomnet omdat op bepaalde momenten de energievraag 
                  van de woning te laag is. <span className="text-destructive font-medium">Zo daalt het rendement.</span>
                </p>
              </div>

              {/* Solution */}
              <div className="space-y-4">
                <h3 className="text-2xl font-serif font-semibold text-foreground">
                  De Loxone oplossing
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Je hebt er dus alle baat bij om de <span className="text-primary font-medium">zelfconsumptie</span> – 
                  het direct gebruiken van de elektriciteit die je zonnepanelen opwekken – te verhogen.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  <span className="text-primary font-medium">Loxone zorgt hiervoor.</span> Onze slimme energie management 
                  systeem zal automatisch verbruikers inschakelen o.b.v. de opbrengst van de PV-installatie.
                </p>
              </div>

              {/* Smart Optimizations */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-foreground">Automatische optimalisaties:</h4>
                <div className="grid gap-3">
                  {energyOptimizations.map((optimization, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-muted/50 to-transparent hover:from-primary/5 hover:to-secondary/5 transition-all duration-300 group cursor-pointer"
                    >
                      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-2 rounded-lg group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                        <optimization.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                          {optimization.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {optimization.description}
                        </div>
                      </div>
                      <CheckCircle className="h-4 w-4 text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Battery Integration Section */}
          <div className="bg-gradient-to-br from-muted/30 to-muted/10 rounded-3xl p-8 md:p-12 mb-16 border border-primary/10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 px-4 py-2 rounded-full text-sm font-medium text-primary mb-4">
                <Battery className="h-4 w-4" />
                Nog beter met een thuisbatterij
              </div>
              <h3 className="text-3xl font-serif font-bold mb-4 text-foreground">
                Perfecte samenwerking met thuisbatterijen
              </h3>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Dankzij Loxone werken je slimme zonnepanelen en thuisbatterij perfect samen. 
                Alle energiestromen worden geoptimaliseerd om jouw elektriciteitsfactuur zo laag mogelijk te houden. 
                <span className="text-primary font-medium">Niet alleen in woningen, maar ook in bedrijfsgebouwen.</span>
              </p>
            </div>

            {/* Energy Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              {energyStats.map((stat, index) => (
                <Card key={index} className="text-center border-primary/10 bg-gradient-to-br from-background/50 to-transparent hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="bg-gradient-to-br from-primary/10 to-secondary/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <stat.icon className="h-8 w-8 text-primary" />
                    </div>
                    <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                    <div className="text-sm text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <h3 className="text-3xl font-serif font-bold mb-4 text-foreground">
              Nu gratis aanvragen
            </h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Dien nu jouw gratis projectaanvraag in of word Loxone Partner in slechts een paar stappen!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary-glow hover:from-primary/90 hover:to-primary-glow/90 text-primary-foreground shadow-energy group"
                onClick={() => {
                  track("energy_management_calendly");
                  (window as any).Calendly?.initPopupWidget?.({
                    url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800'
                  });
                }}
              >
                <Home className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Gratis projectaanvraag
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50"
                asChild
              >
                <a href="/smarthome">
                  <Zap className="mr-2 h-5 w-5" />
                  Meer over Smart Home
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};